import React from 'react';
import { Phone } from 'lucide-react';

/**
 * Nigerian phone input with locked +234 prefix.
 * User enters only the 10-digit local number.
 * Value returned to parent is always the local digits (no prefix).
 * Parent should prepend +234 before sending to API.
 */
const NigerianPhoneInput = ({ value, onChange, required = false, testId = 'phone-input', label = 'Phone Number' }) => {
  const handleChange = (e) => {
    // Allow only digits, max 10
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    onChange(digits);
  };

  const isValid = !required || (value && value.length === 10 && /^[789]/.test(value));
  const showError = value && value.length > 0 && value.length < 10;

  return (
    <div>
      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="flex">
        <div className="flex items-center gap-1.5 bg-[#F3EBE1] border border-r-0 border-[#E6DCD3] rounded-l-lg px-3 select-none">
          <Phone className="h-4 w-4 text-[#3E2723]" />
          <span className="text-sm font-medium text-[#3E2723]">+234</span>
        </div>
        <input
          type="tel"
          inputMode="numeric"
          value={value || ''}
          onChange={handleChange}
          required={required}
          pattern="[789]\d{9}"
          maxLength={10}
          className={`flex-1 px-3 py-3 border border-[#E6DCD3] rounded-r-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none transition-all text-sm ${
            showError ? 'border-red-300' : ''
          }`}
          placeholder="901 234 5678"
          data-testid={testId}
        />
      </div>
      {showError && (
        <p className="text-xs text-red-500 mt-1">Enter a valid 10-digit Nigerian number</p>
      )}
    </div>
  );
};

/**
 * Convert local digits to full international format for API submission
 */
export const formatPhoneForApi = (localDigits) => {
  if (!localDigits) return '';
  const clean = localDigits.replace(/\D/g, '');
  if (clean.length === 10) return `+234${clean}`;
  return localDigits;
};

/**
 * Extract local digits from a stored +234XXXXXXXXXX number for display in the input
 */
export const extractLocalDigits = (fullPhone) => {
  if (!fullPhone) return '';
  const clean = fullPhone.replace(/\D/g, '');
  if (clean.startsWith('234') && clean.length === 13) return clean.slice(3);
  if (clean.startsWith('0') && clean.length === 11) return clean.slice(1);
  if (clean.length === 10) return clean;
  return clean;
};

export default NigerianPhoneInput;
